import React from 'react';
import { Box } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const normalizeInsightMarkdown = (raw = '') => {
    if (!raw) return raw;

    // First decode any HTML entities that might be present
    let text = raw
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');

    // Strip escaped backticks (\`) which render as literal ` characters
    text = text.replace(/\\`/g, '');

    // Convert bold XML tags to markdown equivalents
    text = text.replace(/<b>([\s\S]*?)<\/b>/gi, '**$1**');

    // Aggressively strip any custom sentiment formatting the LLM might have generated 
    // (e.g., <bad>, <good>, or hallucinatory markdown link references like ](#bad))
    text = text
        .replace(/<\/?good>/gi, '')
        .replace(/<\/?bad>/gi, '')
        .replace(/\[(.*?)\]\(#good\)/gi, '$1')
        .replace(/\[(.*?)\]\(#bad\)/gi, '$1')
        .replace(/\]\(#(?:good|bad)\)/gi, '') // catch any dangling end tags
        .replace(/(?<!\[)\[(.*?)\](?![\(\[])/gi, '$1'); // Only unwrap isolated brackets that are NOT part of a markdown link [text](url)

    const lines = text.split(/\r?\n/);
    const out = [];
    let skipRecommendations = false;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();

        // Remove the entire "Actionable Recommendations" block from display (common variants)
        // Examples:
        //   Actionable Recommendations:
        //   **Actionable Recommendations:**
        //   - **Actionable Recommendations:**
        //   - Actionable Recommendations:
        const actionableHeader = (() => {
            const t = trimmed
                .replace(/^[-*•]\s+/, '')
                .replace(/^\*\*(.+)\*\*\s*$/, '$1')
                .replace(/:+\s*$/, '')
                .trim();
            return /^actionable\s+recommendations$/i.test(t);
        })();

        if (actionableHeader) {
            skipRecommendations = true;
            continue;
        }

        if (skipRecommendations) {
            // Stop skipping when we hit a blank line or a new markdown heading
            if (!trimmed) {
                skipRecommendations = false;
                continue;
            }
            if (/^#{1,6}\s+/.test(trimmed)) {
                skipRecommendations = false;
                // fall through so the heading is processed
            } else {
                // Also skip arrow-style recommendation bullets: "→ do something"
                if (/^→\s+/.test(trimmed)) continue;
                // Skip list items under the recommendations block
                if (/^[-*•]\s+/.test(trimmed)) continue;
                continue;
            }
        }

        // ── Skip inline dash-list expansion ─────────────────────────────────────
        // This feature was breaking markdown formatting (e.g., **bold text**)
        // when splitting by " - " patterns. The backend now outputs proper
        // markdown, so this expansion is no longer needed.

        out.push(line);
    }

    // Collapse excessive blank lines
    return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};

const InsightSummary = ({ text, sx = {} }) => {
    if (!text) return null;

    const normalized = normalizeInsightMarkdown(text);

    return (
        <Box 
            sx={{ 
                fontSize: 12.5, 
                lineHeight: 1.75, 
                color: 'text.primary',
                '& .prose': {
                    color: 'inherit',
                    fontSize: 'inherit',
                    lineHeight: 'inherit',
                },
                '& .prose p': {
                    margin: '0 0 0.75rem 0',
                    fontSize: 'inherit',
                    lineHeight: 'inherit',
                },
                '& .prose ul': {
                    margin: '0 0 0.75rem 0',
                    paddingLeft: '1.25rem',
                },
                '& .prose li': {
                    margin: '0.25rem 0',
                    fontSize: 'inherit',
                    lineHeight: 'inherit',
                },
                '& .prose strong': { fontWeight: 700 },
                '& .prose h1, & .prose h2, & .prose h3, & .prose h4, & .prose h5, & .prose h6': {
                    color: '#a78bfa',
                    fontWeight: 600,
                    margin: '0.75rem 0 0.5rem 0',
                    fontSize: '0.95rem',
                },
                '& .prose code': {
                    backgroundColor: 'rgba(99,102,241,0.1)',
                    color: '#6366f1',
                    padding: '0.125rem 0.25rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.875rem',
                },
                ...sx 
            }}
            className="prose prose-sm max-w-none"
        >
            <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                skipHtml={false}
                components={{
                    strong: ({ children }) => (
                        <Box component="span" sx={{ fontWeight: 700 }}>
                            {children}
                        </Box>
                    ),
                    em: ({ children }) => (
                        <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                            {children}
                        </Box>
                    ),
                    a: ({ href, children, ...props }) => (
                        <a href={href} {...props} style={{ color: '#6366f1', textDecoration: 'none' }}>{children}</a>
                    ),
                    ul: ({ children }) => (
                        <Box component="ul" sx={{ 
                            margin: '0.5rem 0', 
                            paddingLeft: '1.25rem',
                            listStyle: 'disc',
                            color: 'text.primary'
                        }}>
                            {children}
                        </Box>
                    ),
                    ol: ({ children }) => (
                        <Box component="ol" sx={{ 
                            margin: '0.5rem 0', 
                            paddingLeft: '1.25rem',
                            color: 'text.primary'
                        }}>
                            {children}
                        </Box>
                    ),
                    li: ({ children }) => (
                        <Box component="li" sx={{ 
                            margin: '0.25rem 0',
                            lineHeight: 1.75,
                            fontSize: '12.5px',
                            color: 'inherit'
                        }}>
                            {children}
                        </Box>
                    ),
                    p: ({ children }) => (
                        <Box component="p" sx={{ 
                            margin: '0 0 0.75rem 0',
                            lineHeight: 1.75,
                            fontSize: '12.5px',
                            color: 'inherit'
                        }}>
                            {children}
                        </Box>
                    ),
                    h1: ({ children }) => (
                        <Box component="h1" sx={{ 
                            fontSize: '1rem', 
                            fontWeight: 600, 
                            color: '#a78bfa',
                            margin: '0.75rem 0 0.5rem 0'
                        }}>
                            {children}
                        </Box>
                    ),
                    h2: ({ children }) => (
                        <Box component="h2" sx={{ 
                            fontSize: '0.95rem', 
                            fontWeight: 600, 
                            color: '#a78bfa',
                            margin: '0.75rem 0 0.5rem 0'
                        }}>
                            {children}
                        </Box>
                    ),
                    h3: ({ children }) => (
                        <Box component="h3" sx={{ 
                            fontSize: '0.9rem', 
                            fontWeight: 600, 
                            color: '#a78bfa',
                            margin: '0.5rem 0 0.25rem 0'
                        }}>
                            {children}
                        </Box>
                    ),
                    code: ({ node, className, children, ...props }) => {
                        // react-markdown v8+ removed the `inline` prop.
                        // A code block always lives inside a <pre>; inline code does not.
                        // We detect block-code by the presence of a language class.
                        const isBlock = /language-\S+/.test(className || '');
                        // Defensively strip any leaked backtick delimiters from children
                        // (can happen when LLM outputs malformed/unbalanced backticks)
                        const safeChildren = typeof children === 'string'
                            ? children.replace(/^`+|`+$/g, '')
                            : children;
                        return (
                            <Box
                                component="code"
                                className={className}
                                sx={{
                                    backgroundColor: isBlock ? 'transparent' : 'rgba(99,102,241,0.1)',
                                    color: '#6366f1',
                                    padding: isBlock ? 0 : '0.125rem 0.35rem',
                                    borderRadius: isBlock ? 0 : '0.25rem',
                                    fontSize: '11.5px',
                                    fontFamily: 'monospace',
                                    display: 'inline',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                                {...props}
                            >
                                {safeChildren}
                            </Box>
                        );
                    },
                    pre: ({ children }) => (
                        <Box component="pre" sx={{
                            backgroundColor: 'rgba(15,23,42,0.6)',
                            color: '#e2e8f0',
                            padding: '0.75rem',
                            borderRadius: '0.5rem',
                            fontSize: '12px',
                            fontFamily: 'monospace',
                            overflowX: 'auto',
                            my: 1,
                            border: '1px solid rgba(99,102,241,0.2)'
                        }}>
                            {children}
                        </Box>
                    ),
                }}
            >
                {normalized}
            </ReactMarkdown>
        </Box>
    );
};

export default InsightSummary;
